/* Utilidades de imagen compartidas por las vistas que generan tarjetas en
 *  cliente (html2canvas): recorte "cover", fondo sólido del tema, optimización
 *  de la foto al subir y el mapeo central slider→factor de zoom.
 *
 *  MOTOR CENTRAL DE EDICIÓN DE IMAGEN (Riki 2026-07-13): toda subida de foto
 *  para crear contenido debe pasar por este módulo (núcleo puro) + el
 *  componente src/components/ImageEditor.tsx (encuadre interactivo). Fase 1
 *  unifica dentro de Podios; luego se porta a AthleteShareModal (Rankings),
 *  que hoy tiene su propia copia de optimizeImage y un encuadre solo-sliders. */

/** Reduce y recomprime una foto UNA vez al subirla (downscale al lado mayor
 *  = maxDim, y JPEG de calidad `quality`). Las fotos de móvil pesan 5-10 MB y
 *  sin esto cada cambio de encuadre/plantilla re-rasteriza lento. La versión
 *  optimizada va SOLO en memoria; nunca se guarda en Firestore. Devuelve el
 *  dataURL original si algo falla (nunca rompe). Espejo de optimizeImage de
 *  AthleteShareModal (rankings) — mantener en sync hasta unificar. */
export function optimizeImage(dataUrl: string, maxDim = 1280, quality = 0.82): Promise<string> {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const scale = Math.min(1, maxDim / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * scale));
      const h = Math.max(1, Math.round(img.height * scale));
      const c = document.createElement('canvas');
      c.width = w;
      c.height = h;
      const ctx = c.getContext('2d');
      if (!ctx) {
        resolve(dataUrl);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      try {
        resolve(c.toDataURL('image/jpeg', quality));
      } catch {
        resolve(dataUrl);
      }
    };
    img.onerror = () => resolve(dataUrl);
    img.src = dataUrl;
  });
}

/** Mapeo ÚNICO del slider de zoom (0-100) al factor multiplicativo que espera
 *  coverCrop: 0 = ×1 (cover base), 100 = ×1.5 (+50%). Centralizado aquí para
 *  que el editor interactivo y el recorte final usen exactamente la misma
 *  escala (evita que se desincronicen si se toca en un sitio y no en el otro). */
export function zoomSliderToFactor(slider: number): number {
  return 1 + slider / 200;
}

/** Recorta una imagen a (targetW × targetH) con lógica "cover" y encuadre
 *  offX/offY (0-100), devolviendo un dataURL. Necesario porque html2canvas no
 *  respeta object-fit:cover y deformaría la foto.
 *  `zoom` escala el recorte: 1 = cover base, >1 acerca (recorte más cerrado),
 *  <1 aleja (recorte más amplio, limitado al tamaño de la imagen). */
export function coverCrop(dataUrl: string, targetW: number, targetH: number, offX = 50, offY = 50, zoom = 1): Promise<string> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const ir = img.width / img.height;
      const tr = targetW / targetH;
      let sw: number, sh: number, sx: number, sy: number;
      if (ir > tr) {
        sh = img.height;
        sw = sh * tr;
      } else {
        sw = img.width;
        sh = sw / tr;
      }
      // Aplica el zoom sobre el recorte cover y lo limita a la imagen real
      // (alejar no puede mostrar más píxeles de los que tiene la foto).
      const z = Math.max(0.01, zoom);
      sw = Math.min(sw / z, img.width);
      sh = Math.min(sh / z, img.height);
      sx = (img.width - sw) * (offX / 100);
      sy = (img.height - sh) * (offY / 100);
      const c = document.createElement('canvas');
      c.width = targetW;
      c.height = targetH;
      const ctx = c.getContext('2d');
      if (!ctx) {
        reject(new Error('Sin contexto de canvas'));
        return;
      }
      ctx.drawImage(img, sx, sy, sw, sh, 0, 0, targetW, targetH);
      resolve(c.toDataURL('image/png'));
    };
    img.onerror = () => reject(new Error('No se pudo cargar la imagen'));
    img.src = dataUrl;
  });
}

/** dataURL de un rectángulo sólido del color del tema (base del vídeo sin
 *  foto). */
export function solidBg(w: number, h: number): string {
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d');
  const bg = getComputedStyle(document.documentElement).getPropertyValue('--ld-bg').trim() || '#000000';
  if (ctx) {
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, w, h);
  }
  return c.toDataURL('image/png');
}
